/**
 * STEP 7: RISK LEDGER
 * 
 * Document risks, unknowns, and potential issues BEFORE they become change orders.
 * Every risk requires a description if selected - no generic checkboxes.
 * 
 * Theme: Spec Ops
 * Based on Document 5 specification
 */

import React, { useState, useMemo } from 'react';
import StepContainer from './StepContainer';
import { RiskModal, RiskCard, RiskSummary } from './risks';
import { RISK_CATEGORIES, IMPACT_LEVELS, getRiskCategory, getImpactLevel } from '../constants/riskCategories';
import { Plus, AlertTriangle, Shield, FileWarning } from 'lucide-react';

const Step7RiskLedger = ({ 
  data = {},
  areas = [],
  onAddRisk,
  onRemoveRisk,
  onUpdate,
  onSave,
  onPrev,
  onNext,
  saving = false,
  isComplete = false,
  validationErrors = [],
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);

  const risks = data.items || [];

  // Handle adding a new risk
  const handleAddRisk = (riskData) => {
    onAddRisk?.(riskData);
    setShowAddModal(false);
  };

  // Handle editing an existing risk
  const handleEditRisk = (riskData) => {
    if (!editingRisk) return;
    
    // Update the risk in the array
    const updatedItems = risks.map(r => 
      r.risk_id === editingRisk.risk_id 
        ? { ...r, ...riskData }
        : r
    );
    
    onUpdate?.('risks', { items: updatedItems });
    setEditingRisk(null);
  };

  // Handle deleting a risk
  const handleDeleteRisk = (riskId) => {
    onRemoveRisk?.(riskId);
  };

  // Calculate summary stats
  const summary = useMemo(() => {
    return {
      total: risks.length,
      high: risks.filter(r => r.impact_level === 'high').length,
      medium: risks.filter(r => r.impact_level === 'medium').length,
      low: risks.filter(r => r.impact_level === 'low').length,
      totalContingency: risks
        .filter(r => r.contingency_included && r.contingency_amount)
        .reduce((sum, r) => sum + (parseFloat(r.contingency_amount) || 0), 0),
    };
  }, [risks]);

  return (
    <StepContainer
      step={7}
      onPrev={onPrev}
      onNext={onNext}
      onSave={onSave}
      isSaving={saving}
      isComplete={isComplete}
      validationErrors={validationErrors}
    >
      <div className="space-y-6 max-w-4xl">
        {/* Intro Section */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-slate-400 text-sm">
              Document known risks and potential issues BEFORE they become surprises.
              Every construction project has unknowns.
            </p>
            <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Each risk requires a specific description - no generic checkboxes.
            </p>
          </div>
          
          {!isComplete && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 
                text-white font-medium transition-colors flex-shrink-0"
              data-testid="add-risk-button"
            >
              <Plus className="w-4 h-4" />
              Add Risk
            </button>
          )}
        </div>

        {/* Risk List */}
        <div className="space-y-4">
          <h3 className="text-amber-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Documented Risks ({risks.length})
          </h3>
          
          {risks.length === 0 ? (
            <div className="p-8 text-center border border-dashed border-slate-700 rounded" data-testid="empty-risks-state">
              <FileWarning className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 font-medium">No risks documented yet</p>
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                Consider documenting: access issues, hidden conditions, electrical/plumbing concerns,
                permit requirements, timeline constraints, or budget risks.
              </p>
              {!isComplete && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="mt-4 px-4 py-2 border border-amber-500/50 text-amber-400 
                    hover:bg-amber-500/10 transition-colors"
                >
                  Add First Risk
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3" data-testid="risks-list">
              {risks.map((risk) => (
                <RiskCard
                  key={risk.risk_id}
                  risk={risk}
                  onEdit={() => setEditingRisk(risk)}
                  onDelete={() => handleDeleteRisk(risk.risk_id)}
                  isLocked={isComplete}
                />
              ))}
            </div>
          )}
        </div>

        {/* Risk Summary */}
        {risks.length > 0 && (
          <RiskSummary risks={risks} />
        )}

        {/* Quick Reference */}
        <div className="p-4 bg-slate-800/30 border border-slate-700 rounded">
          <h4 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Risk Categories Quick Reference
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {RISK_CATEGORIES.slice(0, 12).map(cat => (
              <div 
                key={cat.value}
                className="flex items-center gap-2 text-xs text-slate-500 p-2 bg-slate-800/50 rounded"
              >
                <span className="text-lg">{cat.icon}</span>
                <span>{cat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add Risk Modal */}
      {showAddModal && (
        <RiskModal
          areas={areas}
          onSave={handleAddRisk}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Edit Risk Modal */}
      {editingRisk && (
        <RiskModal
          risk={editingRisk}
          areas={areas}
          onSave={handleEditRisk}
          onClose={() => setEditingRisk(null)}
        />
      )}
    </StepContainer>
  );
};

export default Step7RiskLedger;
